import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Seo, Eyebrow, ArrowLink } from "@/components/site/primitives";
import { Reveal, Stagger, Item } from "@/components/site/motion";
import { PageHero, Section } from "@/components/site/pageparts";
import { POSTS } from "@/data/blog";

const PROGRAMS = [
  { tag: "Program", title: "UPII — Personal", desc: "An open standard and reference implementation for local-first, sovereign personal memory. Core live on PyPI.", to: "/research/upii" },
  { tag: "Program", title: "UEII — Enterprise", desc: "The same guarantees at organizational scale: role-aware memory, the Membrane, and compliance by architecture.", to: "/research/ueii" },
  { tag: "Manifesto", title: "The Memory Problem", desc: "Why memory — not intelligence — is the frontier, and why it belongs at the infrastructure layer.", to: "/research/memory-problem" },
  { tag: "Index", title: "Publications", desc: "Papers, lab notes and reproducible benchmarks. We publish the harness, not just the headline.", to: "/research/publications" },
];

export default function Research() {
  return (
    <div data-testid="research-page">
      <Seo title="Research" description="DataFrontier's research programs on memory as infrastructure — UPII, UEII, and the memory problem." />
      <PageHero
        eyebrow="Research"
        title="We work on one problem: memory as infrastructure."
        lead="Models are no longer the hard part. The hard problems now live in everything around them — memory, identity, and the substrate between a person and the agents that act for them. That is where the work is."
      />
      <Section>
        <Stagger className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-2">
          {PROGRAMS.map((p) => (
            <Item key={p.title}>
              <Link to={p.to} className="group flex h-full flex-col bg-ink p-8 transition-colors duration-300 hover:bg-white/[0.03] md:p-12">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs uppercase tracking-[0.18em] text-saffron">{p.tag}</span>
                  <ArrowUpRight className="h-5 w-5 text-mist transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-bone" />
                </div>
                <h2 className="mt-6 font-serif text-2xl tracking-tight text-bone md:text-3xl">{p.title}</h2>
                <p className="mt-4 max-w-md text-[15px] leading-relaxed text-mist">{p.desc}</p>
              </Link>
            </Item>
          ))}
        </Stagger>
      </Section>

      <Section className="border-t border-white/10">
        <div className="flex items-end justify-between">
          <Reveal>
            <Eyebrow>Selected notes</Eyebrow>
            <h2 className="mt-5 font-serif text-3xl tracking-tighter text-bone md:text-5xl">From the lab</h2>
          </Reveal>
          <Reveal>
            <ArrowLink to="/blog" className="hidden md:inline-flex">All notes</ArrowLink>
          </Reveal>
        </div>
        <Stagger className="mt-14 divide-y divide-white/10 border-t border-white/10">
          {POSTS.slice(0, 4).map((p) => (
            <Item key={p.id}>
              <Link to={`/blog/${p.slug}`} className="group grid gap-4 py-8 md:grid-cols-12 md:gap-8">
                <div className="flex items-center gap-4 font-mono text-xs text-mist md:col-span-3">
                  <span className="text-saffron">{p.id}</span>
                </div>
                <h3 className="font-serif text-xl leading-snug text-bone transition-colors duration-300 group-hover:text-saffron md:col-span-6 md:text-2xl">
                  {p.title}
                </h3>
                <p className="text-sm leading-relaxed text-mist md:col-span-3">{p.abstract.slice(0, 100)}…</p>
              </Link>
            </Item>
          ))}
        </Stagger>
      </Section>
    </div>
  );
}
